import React, { useState } from 'react';
import { Form, Input, Button, message, Alert, Card, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  UserOutlined,
  LockOutlined
} from '@ant-design/icons';
import api from '../utils/axios';

const { Title } = Typography;

const LoginForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isConnectionError, setIsConnectionError] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    setIsConnectionError(false);
   
    try {
      // Test de connexion d'abord
      try {
        await api.get('/health');
      } catch (healthError) {
        setIsConnectionError(true);
      }
     
      // CORRECTION ICI : Envoyer username et password seulement
      const response = await api.post('/api/auth/login', {
        username: values.username.trim().toLowerCase(),
        password: values.password
      }, {
        timeout: 30000
      });
     
      if (response.data.success) {
        message.success('Connexion réussie!');
       
        // Stocker le token et les infos utilisateur
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
       
        // Rediriger vers le dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      } else {
        throw new Error(response.data.message || 'Erreur de connexion');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
     
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        message.error('Impossible de joindre le serveur');
        setIsConnectionError(true);
      } else if (error.response?.status === 401) {
        message.error('Nom d\'utilisateur ou mot de passe incorrect');
      } else if (error.response?.status === 404) {
        message.error('Serveur non disponible');
        setIsConnectionError(true);
      } else {
        message.error(error.response?.data?.message || 'Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.warning('Veuillez remplir tous les champs');
  };

  const retryConnection = () => {
    setIsConnectionError(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <img
              src="/images/latyr.png"
              alt="Logo GST Transport"
              style={styles.logo}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/80/1890ff/ffffff?text=GST";
              }}
            />
          </div>
          <Title level={3} style={styles.title}>
            LATYR GLOBAL BUSINESS
          </Title>
          <p style={styles.subtitle}>
            Système de gestion de transport
          </p>
        </div>

        {isConnectionError && (
          <div style={styles.alertContainer}>
            <Alert
              message="Connexion impossible"
              description="Le serveur ne répond pas. Veuillez réessayer."
              type="error"
              showIcon
              action={
                <Button
                  size="small"
                  type="text"
                  onClick={retryConnection}
                  style={styles.retryButton}
                >
                  Réessayer
                </Button>
              }
            />
          </div>
        )}

        <Form
          name="login"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          size="middle"
          style={styles.form}
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: "Nom d'utilisateur requis" },
              { min: 3, message: "Minimum 3 caractères" }
            ]}
          >
            <Input
              prefix={<UserOutlined style={styles.inputIcon} />}
              placeholder="Nom d'utilisateur ou Email"
              style={styles.input}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Mot de passe requis' },
              { min: 6, message: 'Minimum 6 caractères' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={styles.inputIcon} />}
              placeholder="Mot de passe"
              style={styles.input}
              size="large"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              className="bg-primary-500"
              style={styles.submitButton}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#565e6738',
    padding: '20px',
  },
  loginBox: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    padding: '40px',
    border: '1px solid #ffffffff',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginBottom: '16px',
  },
  logo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  title: {
    margin: '0 0 8px 0',
    color: '#146114ff',
    fontWeight: '600',
  },
  subtitle: {
    color: '#666',
    fontSize: '14px',
    margin: 0,
  },
  alertContainer: {
    marginBottom: '24px',
  },
  retryButton: {
    color: '#058e15ff',
    fontWeight: '500',
  },
  form: {
    marginTop: '8px',
  },
  input: {
    height: '44px',
    borderRadius: '6px',
    borderColor: '#d9d9d9',
  },
  inputIcon: {
    color: '#999',
  },
  submitButton: {
    height: '44px',
    fontSize: '15px',
    borderRadius: '6px',
    borderColor: '#1e8d14ff',
    fontWeight: '500',
    marginTop: '8px',
    transition: 'all 0.3s',
  },
};

export default LoginForm;